import { SendIcon } from '@diligentcorp/atlas-react-icons';
import { IssueStatusChip } from '@issuemanager/apps/client/components';
import {
  AppHeaderPrimaryButtonProps,
  useHeaderStore,
} from '@issuemanager/apps/client/contexts';
import { getIssueHeaderTitle } from '@issuemanager/apps/client/utils';
import { useSaveIssue } from '@issuemanager/client/features/issues';
import {
  IssueActionProgressEnum,
  IssueStatusEnum,
} from '@issuemanager/constants';
import { useEventCallback } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useIssueStatusInfo } from './use-issue-status-info';
import { AssignIssueFormValues } from './use-manage-issue-form';
import useResetFormData from './use-reset-form-data';
import { useIssueOutletContext } from './useIssueOutletContext';

type Params = {
  manageIssueForm: UseFormReturn<
    AssignIssueFormValues,
    any,
    AssignIssueFormValues
  >;
  assignIssueIsLoading: boolean;
  initiateWorkflowIsLoading: boolean;
  manageIssueHandleSubmit: () => void;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  isLoading: boolean;
  activateDeclinePanel: () => void;
  activateApprovePanel: () => void;
  handleSave: () => void;
  saveIssue: ReturnType<typeof useSaveIssue>;
};

export const useHeaderButtons = ({
  manageIssueForm,
  assignIssueIsLoading,
  initiateWorkflowIsLoading,
  manageIssueHandleSubmit,
  isEditing,
  setIsEditing,
  isLoading,
  activateDeclinePanel,
  activateApprovePanel,
  handleSave,
  saveIssue,
}: Params): void => {
  const { t } = useTranslation(['common', 'issue', 'action']);
  const {
    code,
    issue,
    userRoles: issueUserRoles,
    canDiscardIssue,
    onDiscardIssue,
  } = useIssueOutletContext();
  const { setHeaderProps } = useHeaderStore();

  const {
    isInitiated,
    isClosedOrDiscarded,
    isNotYetAssigned,
    isReadyToApprove,
  } = useIssueStatusInfo({
    status: issue.data?.status,
    actionProgress: issue.data?.actionProgress,
    approvalStatus: issue.data?.approvalStatus,
  });

  const { resetForm } = useResetFormData({
    manageIssueForm,
  });

  const handleCancel = useEventCallback(() => {
    setIsEditing(false);
    resetForm();
  });

  const disabledBtns = isLoading;

  const assignButtonProps: AppHeaderPrimaryButtonProps = useMemo(() => {
    let assignButtonLabel: string;
    if (isEditing) {
      assignButtonLabel = assignIssueIsLoading
        ? t('issue:actions.savingAndAssigning')
        : t('issue:actions.saveAndAssign');
    } else {
      assignButtonLabel = assignIssueIsLoading
        ? t('issue:actions.assigning')
        : t('issue:actions.assign');
    }

    return {
      id: 'assign-button',
      variant: 'contained',
      disabled: disabledBtns,
      loading: assignIssueIsLoading,
      onClick: manageIssueHandleSubmit,
      children: assignButtonLabel,
      'data-testid': 'assign-button',
    };
  }, [isEditing, disabledBtns, assignIssueIsLoading, manageIssueHandleSubmit]);

  const initiateButtonProps: AppHeaderPrimaryButtonProps = useMemo(() => {
    const getInitiateButtonLabel = (): string => {
      const canInitiate = isEditing && !isLoading;
      if (initiateWorkflowIsLoading) {
        return canInitiate
          ? t('issue:actions.savingAndInitiating')
          : t('issue:actions.initiating');
      }
      return canInitiate
        ? t('issue:actions.saveAndInitiate')
        : t('issue:actions.initiate');
    };

    return {
      id: 'initiate-button',
      variant: 'contained',
      disabled: disabledBtns,
      loading: initiateWorkflowIsLoading,
      startIcon: <SendIcon />,
      onClick: manageIssueHandleSubmit,
      children: getInitiateButtonLabel(),
      'data-testid': 'initiate-button',
    };
  }, [
    isLoading,
    isEditing,
    initiateWorkflowIsLoading,
    disabledBtns,
    manageIssueHandleSubmit,
  ]);

  const cancelButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      id: 'cancel-button',
      variant: 'text',
      onClick: handleCancel,
      children: t('issue:actions.cancel'),
      'data-testid': 'cancel-button',
      disabled: disabledBtns,
    }),
    [disabledBtns, handleCancel],
  );

  const saveButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      id: 'save-button',
      variant: 'outlined',
      disabled: disabledBtns,
      onClick: handleSave,
      loading: saveIssue.isPending,
      children: saveIssue.isPending
        ? t('issue:actions.saving')
        : t('issue:actions.save'),
      'data-testid': 'save-button',
    }),
    [disabledBtns, handleSave, saveIssue.isPending],
  );

  const editButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      id: 'edit-button',
      variant: 'outlined',
      disabled: disabledBtns,
      onClick: () => setIsEditing(true),
      children: t('issue:actions.edit'),
      'data-testid': 'edit-button',
    }),
    [disabledBtns, setIsEditing],
  );

  const discardButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      children: t('issue:actions.discard'),
      variant: 'text',
      'data-testid': 'discard-button',
      onClick: onDiscardIssue,
      disabled: disabledBtns,
    }),
    [disabledBtns, onDiscardIssue],
  );

  const approveButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      children: t('issue:actions.approve'),
      variant: 'contained',
      'data-testid': 'approve-button',
      onClick: activateApprovePanel,
      disabled: disabledBtns,
    }),
    [disabledBtns],
  );

  const declineButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      children: t('issue:actions.decline'),
      variant: 'outlined',
      'data-testid': 'decline-button',
      onClick: activateDeclinePanel,
      disabled: disabledBtns,
    }),
    [disabledBtns],
  );

  const isIssueTypeManager = Boolean(issueUserRoles.data?.isIssueTypeManager);
  const isIssueManager = Boolean(issueUserRoles.data?.isManager);
  const isIssueOwner = Boolean(issueUserRoles.data?.isOwner);
  const isApprover = Boolean(issueUserRoles.data?.isApprover);
  const isNotYetInitiated =
    issue.data?.status === IssueStatusEnum.IN_PROGRESS &&
    (issue.data?.actionProgress === IssueActionProgressEnum.NOT_INITIATED ||
      issue.data?.actionProgress === IssueActionProgressEnum.BLANK);

  const confirmButton: AppHeaderPrimaryButtonProps | null = useMemo(() => {
    if (isNotYetAssigned || assignIssueIsLoading) {
      if (isIssueTypeManager) {
        return assignButtonProps;
      }
    } else if (isNotYetInitiated) {
      if (isIssueTypeManager || isIssueManager || isIssueOwner) {
        return initiateButtonProps;
      }
    }

    return null;
  }, [
    assignButtonProps,
    initiateButtonProps,
    isNotYetAssigned,
    isNotYetInitiated,
    isIssueTypeManager,
    isIssueManager,
    isIssueOwner,
    assignIssueIsLoading,
  ]);

  const editingButtonsProps: AppHeaderPrimaryButtonProps[] = useMemo(() => {
    let buttons: AppHeaderPrimaryButtonProps[] = [];

    const canEdit =
      (!isInitiated &&
        !isClosedOrDiscarded &&
        (isIssueTypeManager || isIssueManager || isIssueOwner)) ||
      (isInitiated && (isIssueTypeManager || isIssueManager || isIssueOwner));

    if (canEdit) {
      buttons = isEditing
        ? [cancelButtonProps, saveButtonProps]
        : [editButtonProps];
    }

    return buttons;
  }, [
    cancelButtonProps,
    editButtonProps,
    isIssueTypeManager,
    isClosedOrDiscarded,
    isEditing,
    isInitiated,
    isIssueManager,
    isIssueOwner,
    saveButtonProps,
  ]);

  useEffect(() => {
    const issueData = issue.data;
    if (!issueData || issueUserRoles.isFetching) return;

    setHeaderProps({
      viewTitle: {
        breadcrumbSections: [{ url: '/', title: t('common:appName') }],
        title: getIssueHeaderTitle({
          code,
          title: issueData.title,
        }),
      },
      chip: issueData.status ? (
        <IssueStatusChip status={issueData.status} />
      ) : undefined,
      buttons: [
        ...(isReadyToApprove && isApprover && !isEditing
          ? [declineButtonProps]
          : []),
        ...(isReadyToApprove && isApprover && !isEditing
          ? [approveButtonProps]
          : []),
        ...(canDiscardIssue && !isEditing ? [discardButtonProps] : []),
        ...editingButtonsProps,
        ...(confirmButton ? [confirmButton] : []),
      ],
    });
  }, [
    canDiscardIssue,
    code,
    isEditing,
    isReadyToApprove,
    issue.dataUpdatedAt,
    issueUserRoles.isFetching,
    discardButtonProps,
    declineButtonProps,
    confirmButton,
    isApprover,
    approveButtonProps,
    editingButtonsProps,
  ]);
};
